import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isFunction } from 'lodash';
import { compose, setDisplayName } from 'recompose';

import useContext from '../../utils/useContext';
import FocusProvider from './';

const scrollConfig = { block: 'center', behavior: 'smooth' };
const defaultOptions = { focus: true, selectText: true };

const isDom = el => el instanceof Element;

function highlightElement(element) {
  element.classList.add('highlight');

  function onAnimationEnd() {
    element.classList.remove('highlight');
    element.removeEventListener('animationend', onAnimationEnd);
  }

  element.addEventListener('animationend', onAnimationEnd);
}

export const scrollToElement = el =>
  isDom(el) && el.scrollIntoView(scrollConfig);

export const scrollToAndHighlightElement = (el, opts = {}) => {
  const { focus, click, selectText, delay } = { ...defaultOptions, ...opts };

  const onScrollEnd = () => {
    if (isDom(el)) {
      !!focus && el.focus && el.focus();
      !!click && el.click && el.click();
      !!selectText && el.select && el.select();

      highlightElement(el);
    }
  };

  scrollToElement(el);

  // The scrollIntoView behaviour is not normalized
  // across vendors // so this timeout value is a best guess 🤞
  return setTimeout(onScrollEnd, delay || 400);
};

export default (id, propName = 'onFocusRequested') => WrappedComponent =>
  compose(
    setDisplayName('useFocusReceiver'),
    useContext(FocusProvider),
  )(
    class FocusReceiverWrapper extends Component {
      static propTypes = {
        requestFocusOn: PropTypes.func.isRequired,
        subscribeToFocusRequestsOn: PropTypes.func.isRequired,
        subscribeToAnyFocusRequest: PropTypes.func.isRequired,
      };

      constructor(props) {
        super(props);

        this.state = {
          id: this.getId(props),
          propName: this.getPropName(props),
        };
      }

      componentWillReceiveProps(nextProps) {
        const { id: curId, propName: curPropName } = this.state;

        const newId = this.getId(nextProps);
        const newPropName = this.getPropName(nextProps);

        if (curId !== newId || curPropName !== newPropName) {
          curId !== newId && this.unsubscribe && this.unsubscribe();

          this.setState({
            id: this.getId(nextProps),
            propName: this.getPropName(nextProps),
          });
        }
      }

      componentWillUnmount() {
        this.unsubscribe && this.unsubscribe();
      }

      onFocusRequested = (cbOrDomEl, options = {}) => {
        const { id: curId } = this.state;

        if (!curId || this.hasInvalidArgs(cbOrDomEl)) {
          this.unsubscribe && this.unsubscribe();

          return;
        }

        const {
          requestFocusOn,
          subscribeToFocusRequestsOn,
          subscribeToAnyFocusRequest,
        } = this.props;

        let scrollTimeout;

        const callback = fieldId => {
          let perform = cbOrDomEl;

          if (!isFunction(cbOrDomEl)) {
            perform = () => {
              scrollTimeout = scrollToAndHighlightElement(cbOrDomEl, options);
            };
          }

          clearTimeout(scrollTimeout);
          perform({ id: fieldId, scrollToAndHighlightElement, requestFocusOn });
        };

        if (this.isCatchAll && this.unsubscribe) this.unsubscribe();

        const unsubscribe = this.isCatchAll
          ? subscribeToAnyFocusRequest(callback)
          : subscribeToFocusRequestsOn(curId, callback);

        this.unsubscribe = () => {
          clearTimeout(scrollTimeout);
          unsubscribe();
        };
      };

      get isCatchAll() {
        return this.state.id === '*';
      }

      getId = p => (isFunction(id) ? id(p) : id);
      getPropName = p => (isFunction(propName) ? propName(p) : propName);

      hasInvalidArgs = cbOrDomEl =>
        !cbOrDomEl || (this.isCatchAll && !isFunction(cbOrDomEl));

      render() {
        const { propName: curPropName } = this.state;
        const props = { ...this.props, [curPropName]: this.onFocusRequested };

        return <WrappedComponent {...props} />;
      }
    },
  );



// WEBPACK FOOTER //
// ./src/setup/FocusProvider/useFocusReceiver.js